import useDeleteFile from 'hooks/query/s3/useDeleteFile'
import { useUploadFile } from 'hooks/query/s3/useUploadFile'
import { LocalImageUpload } from 'pages/content-video-editor/components/ImageUploadBox/local-image-upload'
import React, {
  CSSProperties,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import { isMobile } from 'react-device-detect'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { CSSTransition } from 'react-transition-group'
import { FormValues } from 'utils/type'
import classNames from 'classnames'
import { ReactComponent as TrashIcon } from 'svg/fi_trash-2.svg'
import { PRODUCT_THUMBNAIL_IMAGE_SLOT_CNT } from 'utils/const'
import { CreateVideoType } from 'constant/videoTypes'
interface FileUploaderProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  index: number
  setSelectedImgs: Dispatch<SetStateAction<{ order: number; imgUrl: string }[]>>
  isAllImgSelect: boolean
  selectedImgs: { order: number; imgUrl: string }[]
  imgCnt: number
  createVideoType: CreateVideoType
}
export const FileUploader = ({
  watch,
  index,
  setValue,
  setSelectedImgs,
  selectedImgs,
  isAllImgSelect,
  imgCnt,
  createVideoType,
}: PropsWithChildren<FileUploaderProps>) => {
  const [isImageDivOpen, setImageDivOpen] = useState<boolean>(false)

  const handleImageDivOpen = () => {
    setImageDivOpen((prev) => !prev)
  }

  const fileInputRef = useRef<(HTMLInputElement | null)[]>([])

  const [selectedOrder, setSelectedOrder] = useState<number[]>([])
  const [images, setImages] = useState<string[]>([])
  const [selectedUploadImages, setSelectedUploadImages] = useState(
    new Array(images.length).fill(true)
  )
  const [uploadImages, setUploadImages] = useState<
    {
      id: number
      file: File
      preview: string
      uploading: boolean
    }[]
  >([])

  const { mutateAsync: uploadFileMutate, isPending } = useUploadFile()
  const { mutateAsync: deleteMutate } = useDeleteFile()

  const handleCheckboxChange = (index: number) => {
    const uploadImgCnt = uploadImages.length ?? 0
    const loadImageCnt = selectedOrder.length ?? 0
    const isAdd = 8 - uploadImgCnt - loadImageCnt > 0

    setSelectedOrder((prev) => {
      if (prev.includes(index)) {
        // 선택 해제: 제거 + 순서 보정
        return prev.filter((i) => i !== index)
      } else if (isAdd) {
        // 새 선택: 뒤에 추가
        return [...prev, index]
      } else {
        return prev
      }
    })
  }

  const handleButtonClick = (refIndex: number) => {
    fileInputRef.current[refIndex]?.click()
  }

  const updateImageArr = (imgUrl: string) => {
    const found = selectedImgs.find((img) => img.imgUrl === imgUrl)

    if (found) {
      // 선택 해제
      const filtered = selectedImgs
        .filter((img) => img.imgUrl !== imgUrl)
        .map((img, idx) => ({ ...img, order: idx + 1 }))

      setSelectedImgs(filtered)
      setValue(
        `products.${index}.selectedThumbnails`,
        filtered.map((i) => i.imgUrl)
      )
    } else {
      const updated = [
        ...selectedImgs,
        { order: selectedImgs.length + 1, imgUrl },
      ]
      setSelectedImgs(updated)
      setValue(
        `products.${index}.selectedThumbnails`,
        updated.map((i) => i.imgUrl)
      )
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files) return
    const MAX_FILE_SIZE_MB = 10
    const selectedFiles = Array.from(event.target.files)
    const originalFile = selectedFiles[0]

    const fileSizeMB = originalFile.size / (1024 * 1024)
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `❌ ${originalFile.name}은(는) ${MAX_FILE_SIZE_MB}MB 초과 파일입니다.`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      return
    }

    const tempId = Date.now() + Math.random()
    const tempPreview = URL.createObjectURL(originalFile)
    setUploadImages((prev) => [
      ...prev,
      {
        file: originalFile,
        preview: tempPreview,
        uploading: true,
        id: tempId,
      },
    ])

    try {
      const { fileUrl } = await uploadFileMutate(originalFile)

      setUploadImages((prev) =>
        prev.map((item) =>
          item.id === tempId
            ? {
                ...item,
                preview: fileUrl,
                uploading: false,
              }
            : item
        )
      )
      updateImageArr(fileUrl)
    } catch (error) {
      toast.error(
        `❌ ${originalFile.name}은(는) upload에 실패했습니다. 다시 시도해주세요!`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        }
      )
      console.log(error)
    } finally {
      URL.revokeObjectURL(tempPreview)
      event.target.value = ''
    }
  }
  const getFileKey = (file: File) =>
    `${file.name}_${file.size}_${file.lastModified}`
  const handleDeleteFile = (targetFile: File, targetUrl: string) => {
    const targetKey = getFileKey(targetFile)
    deleteMutate(targetUrl)
    setUploadImages((prev) =>
      prev.filter((imgObj) => imgObj.preview !== targetUrl)
    )

    setSelectedUploadImages((prev) => {
      const targetIndex = uploadImages.findIndex(
        (imgObj) => getFileKey(imgObj.file) === targetKey
      )
      if (targetIndex === -1) return prev
      const newSelected = [...prev]
      newSelected.splice(targetIndex, 1)
      return newSelected
    })

    // 전체 썸네일 이미지 파일을 관리하는 순서를 정리해줘야한다
    const filtered = selectedImgs
      .filter((img) => img.imgUrl !== targetUrl)
      .map((img, idx) => ({ ...img, order: idx + 1 }))

    setSelectedImgs(filtered)
    setValue(
      `products.${index}.selectedThumbnails`,
      filtered.map((i) => i.imgUrl)
    )
  }

  useEffect(() => {
    if (isAllImgSelect) {
      const updateSelectedImages = uploadImages.map((image, i) => {
        return { order: imgCnt + i + 1, imgUrl: image.preview }
      })
      const newSelectedImages = [...selectedImgs, ...updateSelectedImages]
      setSelectedImgs(newSelectedImages)

      setValue(
        `products.${index}.selectedThumbnails`,
        newSelectedImages.map((el) => el.imgUrl)
      )
    }
  }, [isAllImgSelect])

  return (
    <>
      {/* <div className="flex justify-between md:w-full w-[343px]  bg-[#EEF1F7] items-center px-5 py-[13px] rounded-[10px] mt-2">
        <p className="font-bold text-[14px]">
          [선택] 이미지 업로드{' '}
          {
            watch(`products.${i}.selectedFiles`, []).filter((image) => image)
              .length
          }
          /8
          <span className="font-[400] ml-3">5MB | jpg, png</span>
        </p>
        <button
          className="flex items-center justify-center gap-[8px] w-[65px] h-[44px] md:text-base text-[14px] "
          type="button"
          onClick={() => {
            handleImageDivOpen()
          }}
        >
          <span>{isImageDivOpen ? '접기' : '펼치기'}</span>
          <img
            src="assets/img/icon_up_arrow.png"
            alt="arrow"
            className={`w-[12.8px] h-[8.25px]  transition-transform duration-500 ${
              isImageDivOpen ? '' : 'rotate-180'
            }`}
          />
        </button>
      </div> */}
      {/* <CSSTransition
        in={isImageDivOpen}
        timeout={200}
        classNames="content"
        unmountOnExit
      > */}
      <div
        className="mt-2"
        style={{
          width: '100%',
          background: '#F6F8FB',
          borderRadius: '10px',
          padding: '20px',
          maxHeight: 'calc(2 * 230px)', // 2줄까지만 보이도록 높이 제한 (2줄 높이 + 갭)
          overflowY: 'auto', // 스크롤 가능하게 설정
          scrollbarWidth: 'thin', // Firefox용 기본 스크롤바 폭 설정
          scrollbarColor: '#888 #e0e0e0', // Firefox용 스크롤바 색상 설정
        }}
      >
        <div>
          <p className="font-bold text-[14px] mb-4">
            [선택] 이미지 업로드 {uploadImages.length}
            /8
            <span className="font-[400] ml-3">5MB | jpg, png</span>
          </p>
          <p className="text-[14px] ">
            필수 입력 사항이 아닙니다. 상품 페이지 대표 이미지 외에{' '}
            <span className="text-error font-bold">
              따로 이미지 추가를 원하시는 경우에만{' '}
            </span>
            업로드 해주세요. <br />
            대본 길이에 따라 선택한 이미지가 사용되지 않을 수 있습니다.
          </p>
        </div>
        <div className="grid md:grid-cols-8 grid-cols-3 mt-4 gap-y-2">
          {uploadImages.map((el, index) => {
            const selected = selectedImgs.find(
              (img) => img.imgUrl === el.preview
            )
            const order = selected?.order ?? 0

            return (
              <div
                className={classNames('bg-black shrink-0', {
                  'w-[85px]  relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center':
                    isMobile,
                  'w-[85px]  relative rounded-[4px] border-[1px] border-[#D9DEE8] overflow-hidden flex items-center justify-center ':
                    !isMobile,
                  'h-[151px]':
                    createVideoType === CreateVideoType.PRODUCT_SHORT,
                  'h-[85px]': createVideoType !== CreateVideoType.PRODUCT_SHORT,
                })}
                onClick={() => {
                  updateImageArr(el.preview)
                }}
              >
                {' '}
                {el.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                    <svg
                      className="w-6 h-6 text-white animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                      />
                    </svg>
                  </div>
                )}
                <img src={el.preview} alt={`img_${index + 1}`} />
                <div className="absolute flex items-center cursor-pointer right-1 top-1 z-50 active:scale-95">
                  <div
                    className={
                      'w-[28px] h-[28px] rounded-full border-[1px] bg-[#2E343D] flex items-center justify-center '
                    }
                  >
                    <button
                      className="w-[24px] h-[24px] bg-[#2E343D] rounded-full text-white flex items-center justify-center font-bold "
                      type="button"
                      onClick={() => {
                        handleDeleteFile(el.file, el.preview)
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                <div className="absolute flex items-center cursor-pointer right-1 bottom-1 z-50 active:scale-95">
                  <div
                    className={classNames(
                      'w-[28px] h-[28px] rounded-full border-2 flex items-center justify-center  border-href ',
                      {
                        'opacity-100 bg-white ': order > 0,
                        'bg-[rgba(0,0,0,0.5)] border-white ': order <= 0,
                      }
                    )}
                  >
                    {order > 0 && (
                      <div className="w-[22px] h-[22px] bg-href rounded-full text-white flex items-center justify-center font-bold">
                        {order}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          {new Array(
            PRODUCT_THUMBNAIL_IMAGE_SLOT_CNT -
              selectedOrder.length -
              (uploadImages.length ?? 0)
          )
            .fill(1)
            .map((src, index) => (
              <div key={index} className="cursor-pointer">
                <div className="">
                  <LocalImageUpload
                    handleButtonClick={() => {
                      handleButtonClick(index)
                    }}
                    index={index}
                    isPending={isPending}
                    className={classNames('', {
                      'w-[85px] h-[85px] flex items-center justify-center  bg-white rounded-[4px] flex-col gap-[10px] text-[#8F929B] hover:bg-gray-200':
                        createVideoType !== CreateVideoType.PRODUCT_SHORT,
                      'w-[85px] h-[151px] flex items-center justify-center  bg-white rounded-[4px] flex-col gap-[10px] text-[#8F929B] hover:bg-gray-200':
                        createVideoType === CreateVideoType.PRODUCT_SHORT,
                    })}
                  />
                  <input
                    id={`file-input-${index}`}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileChange}
                    ref={(el) => (fileInputRef.current[index] = el)}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      {/* </CSSTransition> */}
    </>
  )
}
